import { MinimalErrorTracker } from '../../shared/log.js';

import { isTutorialActive } from './tutorial-session-state.js';

const POINTER_EVENTS = ['pointerdown', 'click', 'touchstart'];
const BASE_ALLOWED_SELECTORS = ['.tutorial-overlay__card', '.tutorial-overlay__button'];

const GESTURE_BASE_CLASS = 'tutorial-overlay__gesture';
const GESTURE_VISIBLE_CLASS = 'tutorial-overlay__gesture--visible';
const TUTORIAL_LOG_SCOPE = 'TUTORIAL';
const FALLBACK_VERTICAL_OFFSET_PX = 10;

const MIN_BOARD_GESTURE_PX = 48;
const MAX_BOARD_GESTURE_PX = 164;
const BOARD_GESTURE_MULTIPLIER = 1.35;
const BOARD_GESTURE_OFFSET_PX = 12;

const MIN_UI_GESTURE_PX = 52;
const MAX_UI_GESTURE_PX = 88;
const UI_GESTURE_MULTIPLIER = 0.7;
const UI_GESTURE_OFFSET_PX = 6;

const MIN_GESTURE_TRAVEL_PX = 42;
const MAX_GESTURE_TRAVEL_PX = 132;
const BOARD_GESTURE_TRAVEL_MULTIPLIER = 1;
const BOARD_GESTURE_TRAVEL_OFFSET_PX = 6;
const GESTURE_TRAVEL_STYLE_PROP = '--tutorial-gesture-travel';

let boardMetricsWarningLogged = false;

const DEFAULT_GESTURE_TYPE = 'tap';
const HAND_PATH = `
      <g transform="matrix(1,0,0,1,-235,-1950)">
        <g transform="matrix(2.02015,0,0,2.02017,-51.8034,1393.69)">
          <g transform="matrix(0.66774,0,0,0.667731,-79.3726,-334.953)">
            <path
              d="M528.236,1229.93C512.54,1220.86 485.359,1205.28 469.618,1196.89C455.546,1189.38 435.769,1192.87 422.281,1202.87C407.784,1213.62 400.582,1230.93 412.187,1248.53C420.126,1260.57 432.609,1267.88 446.767,1275.99C465,1286.43 486.195,1298.8 500.555,1330.92C510.393,1352.93 517.483,1373.12 525.225,1390.64C534.889,1412.52 545.695,1430.38 563.034,1443.09C580.302,1455.75 604.185,1463.44 640.6,1464.13C693.224,1465.13 732.789,1451.49 761.402,1428.78C804.342,1394.69 822.859,1339.81 822.859,1281.05C822.859,1281.05 822.859,1177.35 822.859,1177.35C822.859,1153.66 803.621,1134.42 779.926,1134.42C769.102,1134.42 759.208,1138.43 751.659,1145.06C746.564,1127.04 729.984,1113.82 710.34,1113.82C699.881,1113.82 690.29,1117.57 682.844,1123.8C678.9,1104.16 661.543,1089.36 640.755,1089.36C630.684,1089.36 621.419,1092.83 614.102,1098.66L614.102,998.019C614.102,974.323 594.865,955.085 571.169,955.085C547.473,955.085 528.236,974.323 528.236,998.019L528.236,1229.93ZM597.629,1227.11L597.629,998.019C597.629,983.415 585.772,971.559 571.169,971.559C556.566,971.559 544.71,983.415 544.71,998.019L544.71,1244.05C544.71,1247.03 543.115,1249.79 540.529,1251.27C537.943,1252.76 534.76,1252.75 532.183,1251.25C532.183,1251.25 484.795,1223.65 461.865,1211.42C452.977,1206.68 440.613,1209.79 432.094,1216.1C424.606,1221.66 419.946,1230.37 425.94,1239.46C432.512,1249.43 443.234,1254.98 454.953,1261.69C475.607,1273.52 499.328,1287.81 515.595,1324.2C525.444,1346.23 532.543,1366.44 540.294,1383.98C548.689,1402.99 557.711,1418.77 572.773,1429.81C587.907,1440.9 608.996,1447.05 640.913,1447.66C688.883,1448.57 725.077,1436.58 751.159,1415.88C790.26,1384.84 806.385,1334.56 806.385,1281.05C806.385,1281.05 806.385,1177.35 806.385,1177.35C806.385,1162.75 794.529,1150.89 779.926,1150.89C765.323,1150.89 753.466,1162.75 753.466,1177.35L753.428,1263.05C753.428,1267.65 749.694,1271.38 745.095,1271.38C740.495,1271.38 736.761,1267.65 736.761,1263.05L736.8,1156.75C736.8,1142.15 724.944,1130.29 710.34,1130.29C695.737,1130.29 683.881,1142.15 683.881,1156.75L683.862,1245.56C683.862,1250.16 680.128,1253.9 675.528,1253.9C670.929,1253.9 667.195,1250.16 667.195,1245.56L667.214,1132.29C667.214,1117.69 655.358,1105.83 640.755,1105.83C626.151,1105.83 614.295,1117.69 614.295,1132.29L614.295,1227.11C614.295,1231.71 610.561,1235.44 605.962,1235.44C601.363,1235.44 597.629,1231.71 597.629,1227.11Z"
            fill="var(--color-white)"
            stroke="currentColor"
            stroke-width="18"
            stroke-linejoin="round"
            stroke-miterlimit="2"
          />
        </g>
      </g>
    </g>
  `;

const GESTURE_VARIANTS = {
  tap: {
    className: 'tap',
    anchor: { x: 0.48, y: 0.26 },
    offsetY: 12,
    sizeStrategy: 'ui',
    svg: `
      <svg width="64" height="64" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
        ${HAND_PATH}
      </svg>
    `.trim(),
  },
  'swipe-right': {
    className: 'swipe-right',
    anchor: { x: 0.5, y: 0.32 },
    offsetY: 12,
    sizeStrategy: 'board',
    svg: `
      <svg width="64" height="64" viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg">
        ${HAND_PATH}
      </svg>
    `.trim(),
  },
};

const DEFAULT_ANCHORS = {
  board: { x: 0.5, y: 0.3 },
  ui: { x: 0.5, y: 0.26 },
};

const DEFAULT_VERTICAL_OFFSETS = {
  board: 16,
  ui: 12,
};

const DEFAULT_GESTURE_CONFIG = {
  sizeStrategy: 'board',
  anchor: DEFAULT_ANCHORS.board,
  offsetY: DEFAULT_VERTICAL_OFFSETS.board,
};

function clamp(value, min, max) {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function resolveBoardMetrics() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const style = getComputedStyle(document.documentElement);
    const pieceSize = Number.parseFloat(style.getPropertyValue('--piece-size')) || 0;
    const scale = Number.parseFloat(style.getPropertyValue('--board-scale')) || 1;
    if (pieceSize <= 0 || scale <= 0) {
      return null;
    }
    return { pieceRenderSize: pieceSize * scale };
  } catch (error) {
    if (!boardMetricsWarningLogged) {
      boardMetricsWarningLogged = true;
      MinimalErrorTracker.warn(
        TUTORIAL_LOG_SCOPE,
        'Failed to resolve board metrics for tutorial gesture',
        error,
      );
    }
    return null;
  }
}

function computeBoardGestureSize(targetSpan) {
  const metrics = resolveBoardMetrics();
  let renderedPiece = metrics?.pieceRenderSize;
  if (!Number.isFinite(renderedPiece) || renderedPiece <= 0) {
    renderedPiece = Math.max(targetSpan, MIN_BOARD_GESTURE_PX);
  }
  const base = Math.max(
    renderedPiece * BOARD_GESTURE_MULTIPLIER + BOARD_GESTURE_OFFSET_PX,
    renderedPiece * 1.2,
  );
  return clamp(base, MIN_BOARD_GESTURE_PX, MAX_BOARD_GESTURE_PX);
}

function computeUiGestureSize(targetSpan) {
  const span = Math.max(targetSpan, MIN_UI_GESTURE_PX);
  const base = Math.max(span * UI_GESTURE_MULTIPLIER + UI_GESTURE_OFFSET_PX, span * 0.85);
  return clamp(base, MIN_UI_GESTURE_PX, MAX_UI_GESTURE_PX);
}

function computeGestureTravel(targetSpan) {
  const metrics = resolveBoardMetrics();
  const renderedSize = metrics?.pieceRenderSize;
  const baseSpan =
    Number.isFinite(renderedSize) && renderedSize > 0
      ? renderedSize
      : Math.max(targetSpan, MIN_GESTURE_TRAVEL_PX);
  const travel = baseSpan * BOARD_GESTURE_TRAVEL_MULTIPLIER + BOARD_GESTURE_TRAVEL_OFFSET_PX;
  return clamp(travel, MIN_GESTURE_TRAVEL_PX, MAX_GESTURE_TRAVEL_PX);
}

let maskRoot = null;
let cardRoot = null;
let gestureEl = null;
let focusedTargetEl = null;
let currentGestureSelector = null;
let currentGestureType = null;
let currentGestureAnchor = DEFAULT_GESTURE_CONFIG.anchor;
let currentGestureSizeStrategy = DEFAULT_GESTURE_CONFIG.sizeStrategy;
let currentGestureVerticalOffset = DEFAULT_GESTURE_CONFIG.offsetY;
let repositionBound = false;
let repositionScheduled = false;
let pointerGuardsAttached = false;
let activeAllowedSelectors = [...BASE_ALLOWED_SELECTORS];

function setFocusedTarget(element) {
  if (focusedTargetEl === element) {
    return;
  }
  if (focusedTargetEl && focusedTargetEl.classList) {
    focusedTargetEl.classList.remove('tutorial-overlay__focus');
  }
  focusedTargetEl = element instanceof HTMLElement ? element : null;
  if (focusedTargetEl) {
    focusedTargetEl.classList.add('tutorial-overlay__focus');
  }
}

function ensureMaskRootInternal() {
  if (typeof document === 'undefined') {
    return { mask: null };
  }
  if (!maskRoot) {
    maskRoot = document.createElement('div');
    maskRoot.className = 'tutorial-overlay';
    document.body.appendChild(maskRoot);
  }
  return { mask: maskRoot };
}

function ensureGestureElement() {
  if (typeof document === 'undefined') {
    return null;
  }
  const { mask } = ensureMaskRootInternal();
  if (!mask) return null;
  if (!gestureEl) {
    gestureEl = document.createElement('div');
    gestureEl.className = GESTURE_BASE_CLASS;
    gestureEl.setAttribute('aria-hidden', 'true');
    mask.appendChild(gestureEl);
  }
  return gestureEl;
}

function ensureCardRootInternal() {
  if (typeof document === 'undefined') {
    return null;
  }
  if (!cardRoot) {
    cardRoot = document.createElement('div');
    cardRoot.className = 'tutorial-overlay__content';
    document.body.appendChild(cardRoot);
  }
  return cardRoot;
}

function clearContent() {
  const root = ensureCardRootInternal();
  if (root) {
    root.innerHTML = '';
  }
}

function pointerGuardHandler(event) {
  if (!isTutorialActive()) {
    return;
  }

  const allowed = activeAllowedSelectors.some((selector) => {
    if (typeof selector !== 'string') return false;
    return event.target instanceof Element && event.target.closest(selector);
  });

  if (!allowed) {
    event.preventDefault();
    event.stopPropagation();
  }
}

function updateGesturePositionNow() {
  if (!gestureEl || !currentGestureSelector) {
    if (gestureEl) {
      gestureEl.style.removeProperty(GESTURE_TRAVEL_STYLE_PROP);
      gestureEl.style.display = 'none';
      gestureEl.classList.remove(GESTURE_VISIBLE_CLASS);
    }
    setFocusedTarget(null);
    return;
  }

  const target = document.querySelector(currentGestureSelector);
  if (!target) {
    gestureEl.style.removeProperty(GESTURE_TRAVEL_STYLE_PROP);
    gestureEl.style.display = 'none';
    gestureEl.classList.remove(GESTURE_VISIBLE_CLASS);
    setFocusedTarget(null);
    MinimalErrorTracker.warn(
      TUTORIAL_LOG_SCOPE,
      `Target not found for gesture: ${currentGestureSelector}`,
    );
    return;
  }

  setFocusedTarget(target);

  const rect = target.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const centerX = Math.min(Math.max(rect.left + rect.width / 2, 16), viewportWidth - 16);
  const centerY = Math.min(Math.max(rect.top + rect.height / 2, 16), viewportHeight - 16);

  gestureEl.style.display = 'block';
  const targetSpan = Math.max(rect.width, rect.height);
  const desiredSize =
    currentGestureSizeStrategy === 'board'
      ? computeBoardGestureSize(targetSpan)
      : computeUiGestureSize(targetSpan);
  gestureEl.style.width = `${desiredSize}px`;
  gestureEl.style.height = `${desiredSize}px`;

  const travelDistance =
    currentGestureType === 'swipe-right' ? computeGestureTravel(targetSpan) : 0;
  gestureEl.style.setProperty(GESTURE_TRAVEL_STYLE_PROP, `${travelDistance}px`);

  const width = gestureEl.offsetWidth || desiredSize;
  const height = gestureEl.offsetHeight || desiredSize;
  const anchorX = currentGestureAnchor?.x ?? 0.5;
  const anchorY = currentGestureAnchor?.y ?? 0.5;
  const adjustedLeft = centerX + (0.5 - anchorX) * width;
  const adjustedTop = centerY + (0.5 - anchorY) * height;

  gestureEl.style.left = `${adjustedLeft}px`;
  gestureEl.style.top = `${adjustedTop + currentGestureVerticalOffset}px`;
  gestureEl.classList.add(GESTURE_VISIBLE_CLASS);
}

export function scheduleGestureUpdate() {
  if (!gestureEl || !currentGestureSelector) {
    return;
  }
  if (repositionScheduled) return;
  repositionScheduled = true;
  requestAnimationFrame(() => {
    repositionScheduled = false;
    updateGesturePositionNow();
  });
}

export function ensureMaskRoot() {
  return ensureMaskRootInternal();
}

export function ensureCardRoot() {
  return ensureCardRootInternal();
}

export function clearOverlay() {
  if (maskRoot && typeof maskRoot.remove === 'function') {
    maskRoot.remove();
  }
  if (cardRoot && typeof cardRoot.remove === 'function') {
    cardRoot.remove();
  }
  maskRoot = null;
  cardRoot = null;
  gestureEl = null;
  setFocusedTarget(null);
  currentGestureSelector = null;
  currentGestureType = null;
  currentGestureAnchor = DEFAULT_GESTURE_CONFIG.anchor;
  currentGestureSizeStrategy = DEFAULT_GESTURE_CONFIG.sizeStrategy;
  currentGestureVerticalOffset = DEFAULT_GESTURE_CONFIG.offsetY;
}

export function setOverlayDim(isDimmed) {
  const { mask } = ensureMaskRootInternal();
  if (!mask) return;
  mask.classList.toggle('tutorial-overlay--dim', Boolean(isDimmed));
}

export function setHighlightTarget(selector) {
  if (typeof document === 'undefined') {
    return;
  }
  if (!selector || typeof selector !== 'string') {
    setFocusedTarget(null);
    return;
  }
  const target = document.querySelector(selector);
  if (!target) {
    setFocusedTarget(null);
    MinimalErrorTracker.warn(TUTORIAL_LOG_SCOPE, `Target not found for focus: ${selector}`);
    return;
  }
  setFocusedTarget(target);
  if (currentGestureSelector) {
    scheduleGestureUpdate();
  }
}

export function renderCard({ message, buttons = [] }) {
  const root = ensureCardRootInternal();
  if (!root) return;

  clearContent();

  const card = document.createElement('div');
  card.className = 'tutorial-overlay__card';
  card.tabIndex = -1;

  const lines = Array.isArray(message) ? message : [message];
  lines.filter(Boolean).forEach((line, index) => {
    const body = document.createElement('p');
    body.className = 'tutorial-overlay__body';
    body.textContent = line;
    if (index > 0) {
      body.classList.add('tutorial-overlay__body--sub');
    }
    card.appendChild(body);
  });

  if (buttons.length > 0) {
    const actions = document.createElement('div');
    actions.className = 'tutorial-overlay__actions';
    buttons.forEach(({ label, onClick, variant = 'secondary' }) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `tutorial-overlay__button tutorial-overlay__button--${variant}`;
      btn.textContent = label;
      btn.addEventListener('click', () => onClick?.());
      actions.appendChild(btn);
    });
    card.appendChild(actions);
  }

  root.appendChild(card);
  requestAnimationFrame(() => card.focus());
}

export function setAllowedSelectors(selectors = []) {
  const normalized = [...BASE_ALLOWED_SELECTORS];
  selectors.filter(Boolean).forEach((selector) => normalized.push(selector));
  activeAllowedSelectors = Array.from(new Set(normalized));
}

export function attachPointerGuards() {
  if (pointerGuardsAttached || typeof document === 'undefined') {
    return;
  }
  POINTER_EVENTS.forEach((type) => document.addEventListener(type, pointerGuardHandler, true));
  pointerGuardsAttached = true;
}

export function detachPointerGuards() {
  if (!pointerGuardsAttached || typeof document === 'undefined') {
    return;
  }
  POINTER_EVENTS.forEach((type) => document.removeEventListener(type, pointerGuardHandler, true));
  pointerGuardsAttached = false;
}

export function attachRepositionListeners() {
  if (repositionBound || typeof window === 'undefined') {
    return;
  }
  const handler = () => scheduleGestureUpdate();
  window.addEventListener('resize', handler);
  window.addEventListener('scroll', handler, true);
  repositionBound = true;
  attachRepositionListeners._handler = handler;
}

export function detachRepositionListeners() {
  if (!repositionBound || typeof window === 'undefined') {
    return;
  }
  const handler = attachRepositionListeners._handler;
  if (handler) {
    window.removeEventListener('resize', handler);
    window.removeEventListener('scroll', handler, true);
  }
  repositionBound = false;
  repositionScheduled = false;
  attachRepositionListeners._handler = null;
}

function resolveGestureConfig(type) {
  if (!type || typeof type !== 'string') {
    return GESTURE_VARIANTS[DEFAULT_GESTURE_TYPE];
  }
  return GESTURE_VARIANTS[type] ?? GESTURE_VARIANTS[DEFAULT_GESTURE_TYPE];
}

export function showGesture(selector, type = DEFAULT_GESTURE_TYPE) {
  if (typeof document === 'undefined') {
    return;
  }
  if (!selector || typeof selector !== 'string') {
    hideGesture();
    return;
  }

  const element = ensureGestureElement();
  if (!element) {
    return;
  }

  const normalizedType = typeof type === 'string' ? type : DEFAULT_GESTURE_TYPE;
  const config = resolveGestureConfig(normalizedType);
  const previousType = currentGestureType;

  if (previousType !== normalizedType || element.innerHTML.trim().length === 0) {
    element.innerHTML = config.svg;
  }

  element.dataset.gestureType = normalizedType;
  element.className = `${GESTURE_BASE_CLASS} ${GESTURE_BASE_CLASS}--${config.className}`;

  const sizeStrategy = config.sizeStrategy ?? DEFAULT_GESTURE_CONFIG.sizeStrategy;
  const anchorDefaults = DEFAULT_ANCHORS[sizeStrategy] ?? DEFAULT_GESTURE_CONFIG.anchor;
  const offsetDefaults = DEFAULT_VERTICAL_OFFSETS[sizeStrategy] ?? FALLBACK_VERTICAL_OFFSET_PX;
  currentGestureSelector = selector;
  currentGestureType = normalizedType;
  currentGestureSizeStrategy = sizeStrategy;
  currentGestureAnchor = config.anchor ?? anchorDefaults;
  currentGestureVerticalOffset = Number.isFinite(config.offsetY) ? config.offsetY : offsetDefaults;

  scheduleGestureUpdate();
}

export function hideGesture() {
  currentGestureSelector = null;
  currentGestureType = null;
  currentGestureAnchor = DEFAULT_GESTURE_CONFIG.anchor;
  currentGestureSizeStrategy = DEFAULT_GESTURE_CONFIG.sizeStrategy;
  currentGestureVerticalOffset = DEFAULT_GESTURE_CONFIG.offsetY;
  if (gestureEl) {
    gestureEl.style.display = 'none';
    gestureEl.classList.remove(GESTURE_VISIBLE_CLASS);
    gestureEl.removeAttribute('data-gesture-type');
    gestureEl.style.removeProperty(GESTURE_TRAVEL_STYLE_PROP);
  }
}
